import { type ReactNode, useState } from "react";
import { cn } from "../../utils";
import { Icon } from "../../icons";
import type { ToolCallStatus } from "../../api/contracts/chat";
import { ToolDiff, isDiffTool } from "./tool-diff";

/**
 * One tool call in a Copilot turn — the quiet sibling of an activity row. Reads
 * as a single compact line: a monospace tool-name chip, a one-line target/summary,
 * and a status dot (allowed/ran = success, denied/failed = destructive, proposed =
 * muted). A denied call surfaces its `verdict_reason` muted below. Uses the shared
 * list-row pattern so a stack of steps reads as one quiet list. Dot colors come
 * from the intent tokens, never a raw palette hue.
 */

const DOT: Record<ToolCallStatus, string> = {
  proposed: "bg-muted-foreground/50",
  allowed: "bg-success",
  ran: "bg-success",
  denied: "bg-destructive",
  failed: "bg-destructive",
};

export function ToolCallStep({
  name,
  summary,
  status,
  reason,
  input,
  expanded,
}: {
  name: string;
  summary?: string;
  status: ToolCallStatus;
  reason?: string | null;
  /** Raw tool input — when present + `expanded`, renders a diff (Edit/Write) or
   *  the input JSON so the user can watch the work (P1.2 / P4.2). */
  input?: unknown;
  expanded?: boolean;
}) {
  const showReason = status === "denied" && reason && reason.trim().length > 0;
  const showDiff = expanded && input !== undefined && isDiffTool(name);
  return (
    <div className="border-b border-border/40 py-1.5 first:pt-0 last:border-b-0">
      <div className="flex items-center gap-2">
        <span
          className={cn("size-1.5 shrink-0 rounded-full", DOT[status])}
          aria-hidden
        />
        {/* badge-ignore: monospace tool-call identifier (e.g. db_query), not a status tag — must stay lowercase + mono */}
        <span className="shrink-0 rounded bg-secondary/60 px-1.5 py-0.5 font-mono type-subtext text-foreground">
          {name}
        </span>
        {summary ? (
          <span className="min-w-0 flex-1 truncate type-subtext text-muted-foreground">
            {summary}
          </span>
        ) : null}
      </div>
      {showDiff ? <ToolDiff name={name} input={input} /> : null}
      {showReason ? (
        <p className="mt-0.5 pl-3.5 type-subtext text-muted-foreground">{reason}</p>
      ) : null}
    </div>
  );
}

/**
 * Collapses a turn's tool calls into a single disclosure line ("Used N tools")
 * so a finished answer reads as one clean response, not a stack of plumbing.
 * Collapsed by default; the steps live behind the toggle.
 */
export function ToolCallGroup({
  count,
  children,
}: {
  count: number;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  if (count === 0) return null;
  return (
    <div>
      {/* button-ignore: quiet text disclosure toggle (like the rail's "Clear all"), not an emphasis-graded action button */}
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex items-center gap-1 type-subtext text-muted-foreground transition-colors hover:text-foreground"
      >
        <Icon name={open ? "chevron-down" : "chevron-right"} size="xs" />
        {open ? "Hide" : "Used"} {count} tool{count === 1 ? "" : "s"}
      </button>
      {open ? <div className="mt-1">{children}</div> : null}
    </div>
  );
}
